import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import objectPath from 'object-path';
import PropTypes from 'prop-types';
import { Spinner } from 'components';
import { Center } from 'legacyComponents/styled/lib';
import {
  Option,
  OptionsList,
  OptionWrapper,
  sizeConfig,
} from 'libs/forms/styles';
import { ComponentRegistry } from 'core/index';
// add a "loadingData" field to input field e.g. selectables

export const DefaultOption = observer(
  ({ app, highlighted, labelKey, onMouseOver, onClick, scrollRef }) => {
    if (!labelKey) {
      console.error(
        'Please provide a "labelKey" for field with DropDown Options',
      );
    }
    const labelText = objectPath.get(app, labelKey);
    return (
      <OptionWrapper
        ref={scrollRef}
        highlighted={highlighted}
        onMouseOver={onMouseOver}
        onClick={onClick}
      >
        <Option highlighted={highlighted}>{labelText}</Option>
      </OptionWrapper>
    );
  },
);

const SelectableList = observer(props => {
  const {
    appKey,
    manager,
    labelKey,
    select,
    highlighted,
    Element,
    selectables,
    setHighlightedRef,
    scrollToHighlighted,
    singleIndent = false,
  } = props;
  const { nestedSelectables } = manager?.conf;
  const selectedText = manager?.fieldStore.inputValue;
  useEffect(() => {
    if (
      selectables.length === 1 &&
      manager?.SetExpandedApp &&
      manager?.expandedApp?.name !== 'Recommended'
    ) {
      manager?.SetExpandedApp(selectables.index(0), true, false);
    }
  }, [selectables.length]);
  useEffect(() => {
    scrollToHighlighted();
  }, [highlighted, manager?.highlightedToExpand]);

  const SelectableElements = selectables.map(app => {
    if (app.isHeader) {
      return (
        <OptionWrapper key={app[appKey]} heading>
          <Option heading>{app[labelKey]}</Option>
        </OptionWrapper>
      );
    }
    const { disabled } = app;
    let subElements = null;
    const expanded =
      app.isExpandable && manager?.expandedPath?.includes(app.id);

    let categoryApp = null;
    if (app.isSelectableCategory) {
      categoryApp = app;
    }
    if (expanded) {
      /* Could eliminate many parameters by using the manager's properties (also in <Element>).
      But then important to define a clear typed interface to SelectableList
      to avoid implicit passing of variables */
      subElements = (
        <SelectableList
          appKey={appKey}
          key={`list-${app[appKey]}`}
          labelKey={labelKey}
          select={select}
          highlighted={highlighted}
          Element={Element}
          manager={manager}
          selectables={app[nestedSelectables]}
          nestedSelectables={nestedSelectables}
          setHighlightedRef={setHighlightedRef}
          categoryApp={categoryApp}
          scrollToHighlighted={scrollToHighlighted}
          singleIndent={app.name === 'Recommended'}
        />
      );
    }

    let isHighlighted = false;
    if (app instanceof Object) {
      isHighlighted =
        app[appKey] !== undefined && app[appKey] === highlighted?.[appKey];
    } else {
      isHighlighted = app === highlighted;
    }
    let isHighlightedToExpand;
    if (typeof app === 'object') {
      isHighlightedToExpand =
        app[appKey] !== undefined &&
        app[appKey] === manager.highlightedToExpand?.[appKey];
    }
    const scrollRef =
      (!categoryApp && isHighlighted) || isHighlightedToExpand
        ? setHighlightedRef
        : () => {};
    return (
      <React.Fragment key={app[appKey]}>
        <Element
          app={app}
          scrollRef={scrollRef}
          labelKey={labelKey}
          appKey={appKey}
          singleItem={
            manager?.IsSingleExpandable && manager.IsSingleExpandable(app)
          }
          expanded={expanded}
          highlighted={!categoryApp && isHighlighted}
          highlightedToExpand={isHighlightedToExpand}
          key={app[appKey]}
          selection={selectedText}
          singleIndent={singleIndent}
          onMouseOver={() => {
            if (!manager.keyboardMode) {
              // eslint-disable-next-line no-param-reassign
              app.categoryAppMode = false;
              manager.Highlight(app);
            }
          }}
          onClick={() => {
            if (app.isExpandable) {
              if (!manager?.IsSingleExpandable(app))
                manager.SetExpandedApp(app, false, false);
            } else if (!disabled) select();
          }}
        />
        {subElements}
      </React.Fragment>
    );
  });
  // add a selectable representing the Category i.e. "Any Aluminium"
  if (props.categoryApp) {
    let isHighlighted;
    const app = props.categoryApp;
    if (app instanceof Object) {
      isHighlighted =
        app[appKey] !== undefined && app[appKey] === highlighted?.[appKey];
    } else {
      isHighlighted = app === highlighted;
    }
    const { disabled } = app;
    const scrollRef = isHighlighted ? setHighlightedRef : () => {};
    SelectableElements.unshift(
      <Element
        scrollRef={scrollRef}
        app={app}
        labelPrefix='Any '
        labelKey={labelKey}
        appKey={appKey}
        highlighted={isHighlighted}
        key={`any-${app[appKey]}`}
        selection={selectedText}
        isSelectableForEntireCategory
        onMouseOver={() => {
          if (!manager.keyboardMode) {
            app.categoryAppMode = true;
            manager.Highlight(app);
          }
        }}
        onClick={() => {
          if (!disabled) select();
        }}
      />,
    );
  }
  return SelectableElements;
});

@observer
export class Dropdown extends React.Component {
  highjackOnKeyDown = () => {
    const { inputFields, fieldName } = this.props;
    this.inputField = inputFields[fieldName];
    if (this.inputField) {
      this.keyDown = this.inputField.onKeyDown;
      this.inputField.onKeyDown = this.onKeyDown;
    }
  };

  componentDidMount() {
    this.highjackOnKeyDown();
    const { manager } = this.props;
    if (manager && !manager.conf.allowNew) {
      manager.highlightFirst();
    }
    this.dropUp = false;
    // this.scrollToHighlighted()
  }

  componentDidUpdate() {
    /*
     * nasty hack, we must wait for the component to have updated
     * in order for the Apps to be created so we call highlightFirst everytime the component is updated
     */
    const { manager } = this.props;
    if (manager && !manager.conf.allowNew) {
      manager.highlightFirst();
    }
  }

  scrollToHighlighted = () => {
    if (
      (this.props.manager?.highlighted ||
        this.props.manager?.highlightedToExpand) &&
      this.ref &&
      this.highlightedRef
    ) {
      const elHeight = this.highlightedRef.offsetHeight;
      const ddHeight = this.ref.offsetHeight;
      const highlightedOffset = this.highlightedRef.offsetTop;
      const difference = highlightedOffset - this.ref.scrollTop;
      const elementCenterOffset = ddHeight / 2 - elHeight / 2;
      if (this.props.manager.keyboardMode) {
        this.ref.scrollTop = highlightedOffset - elementCenterOffset;
      } else if (difference > ddHeight) {
        // center out of view highlighted elements
        this.ref.scrollTop = highlightedOffset - elementCenterOffset;
      } else if (difference > ddHeight - elHeight) {
        // When scrolling down always show the whole element when it is highlighted
        this.ref.scrollTop = highlightedOffset - ddHeight + elHeight;
      } else if (difference < 0) {
        // when scrolling up always show the whole element when it is highlighted
        this.ref.scrollTop = highlightedOffset;
      }
    }
  };

  setHighlightedRef = ref => {
    this.highlightedRef = ref;
  };

  select = () => {
    this.props.manager.Select();
    if (this.inputField.focusNext) {
      this.inputField.focusNext();
    }
  };

  onKeyDown = e => {
    const { manager } = this.props;
    if (manager) {
      // && manager.active) {
      if (e.which === 38) {
        manager.highlightPrev();
        e.preventDefault();
        return;
      }
      if (e.which === 40) {
        manager.highlightNext();
        e.preventDefault();
        return;
      }
      if (e.which === 13 || e.which === 9) {
        e.preventDefault();
        if (manager.highlighted) {
          this.select(e);
          return;
        }
        const toExpand = manager.highlightedToExpand;
        if (toExpand && !manager?.IsSingleExpandable(toExpand)) {
          manager.SetExpandedApp(toExpand, false, true);
          return;
        }
        if (!manager.conf.allowNew) {
          return;
        }
      }
      this.keyDown(e);
    }
  };

  render() {
    const { manager } = this.props;
    if (!manager?.showOptions) {
      return null;
    }
    let Element = ComponentRegistry.get(manager.conf.optionComponent);
    if (!Element) {
      if (manager.conf.optionComponent) {
        console.warn(
          `${manager.conf.optionComponent} not found in ComponentRegistry`,
        );
      }
      Element = DefaultOption;
    }

    const { appKey, labelKey, label } = manager.fieldStore.conf;
    // using manager.highlighted rather than app.highlighted causes
    // unnecessary re-rendering. It would be nice to add arbitrary properties to adapt
    // the app to context adding highlighted and selected.

    const { heading } = manager.conf;
    const noResultsMessage = manager.loading ? (
      <Center>
        <Spinner size='small' margin='1rem' />
      </Center>
    ) : (
      <Option disabled>No options available for {label}.</Option>
    );
    const noResults = (
      <OptionWrapper key='none-available' heading>
        {noResultsMessage}
      </OptionWrapper>
    );

    const shouldDropUp = () => {
      if (this?.ref) {
        this.dropUp =
          this?.ref?.getBoundingClientRect()?.top +
            (this.dropUp
              ? sizeConfig.dropHeight * 2 +
                (sizeConfig.inputFormMinHeight - sizeConfig.labelHeight)
              : sizeConfig.dropHeight) >=
          window.innerHeight;
      }

      return this.dropUp;
    };

    return (
      <OptionsList
        ref={ref => {
          this.ref = ref;
        }}
        dropUp={shouldDropUp()}
        onMouseDown={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {heading && manager?.selectables?.length > 0 && (
          <OptionWrapper key={heading} heading>
            <Option heading>{heading}</Option>
          </OptionWrapper>
        )}
        {(manager?.selectables?.length > 0 && (
          <SelectableList
            appKey={appKey}
            labelKey={labelKey}
            key='list-top-level'
            select={this.select}
            highlighted={manager.highlighted}
            Element={Element}
            manager={manager}
            selectables={manager.selectables}
            setHighlightedRef={this.setHighlightedRef}
            scrollToHighlighted={this.scrollToHighlighted}
          />
        )) ||
          noResults}
      </OptionsList>
    );
  }
}
Dropdown.propTypes = {
  manager: PropTypes.object,
  inputFields: PropTypes.object,
  fieldName: PropTypes.string,
};
